// galaxy/model.js: the Milky Way model shared by every galaxy pass.
//
// Frame (used everywhere): Galactocentric, units = kpc, y = north Galactic
// pole. Sun at (0, Z_SUN, R0). beta = Galactocentric azimuth as in Reid et
// al. 2019: 0 toward the Sun, increasing in the direction of rotation
// (clockwise seen from the NGP). phi = atan2(z, x) = beta + 90 deg.
// Galactic longitude l = 90 deg points to -x at the Sun, the centre to -z.
import * as THREE from 'three';

export const D2R = Math.PI / 180;
export const R0 = 8.178;          // kpc, GRAVITY Collaboration 2019, A&A 625, L10
export const Z_SUN = 0.0208;      // kpc above the plane (Bennett & Bovy 2019, MNRAS 482, 1417)
export const BAR_ANGLE = 27;      // deg, bar near end to the Sun-centre line (Wegg+2015: 28-33, B-H&G 2016: 27+-2)
export const BAR_HALF = 5.0;      // kpc, long-bar half-length (Wegg, Gerhard & Portail 2015)
export const MAP_R = 18;          // kpc, half-size of the face-on maps
export const SUN = new THREE.Vector3(0, Z_SUN, R0);
export const BAR_PHI = (BAR_ANGLE + 90) * D2R; // world phi of the bar's near end

// Spiral arms: log-periodic segments through (beta deg, R kpc) points, from
// Reid et al. 2019, ApJ 885, 131, Table 2 (R_kink, beta_kink, pitch angles
// either side of the kink). Beyond the maser data the arms are extended the
// way that paper does (Carina at 10 deg pitch, Sgr and Norma in to the bar);
// the two major arms start at the bar ends (Benjamin 2008 / Churchwell 2009
// picture). w = intrinsic 1-sigma width at the kink (kpc).
// Amplitudes: old = old-star (stellar density) arm, young = OB stars,
// dust = dust lane, hii = HII regions. Artistic weights.
export const ARMS = [
  { name: 'Scutum-Centaurus', pts: [[72, 4.12], [30, 4.80], [23, 4.93], [-40, 6.50], [-250, 14.2]], w: 0.23, old: 1.0, young: 1.3, dust: 1.0, hii: 1.0, major: 1 },
  { name: 'Perseus', pts: [[252, 4.12], [207, 5.0], [115, 7.28], [40, 8.90], [-23, 10.87], [-135, 15.5]], w: 0.35, old: 1.0, young: 1.2, dust: 1.0, hii: 0.9, major: 1 },
  { name: 'Norma-Outer', pts: [[54, 3.58], [18, 4.475], [0, 4.455], [-342, 12.28], [-395, 12.9]], w: 0.2, old: 0.12, young: 0.6, dust: 0.6, hii: 0.6 },
  { name: 'Sagittarius-Carina', pts: [[225, 4.24], [97, 5.93], [24, 6.06], [-33, 8.23], [-230, 15.1]], w: 0.27, old: 0.15, young: 0.75, dust: 0.75, hii: 0.9 },
  { name: 'Local', pts: [[52, 7.12], [9, 8.26], [-34, 9.63]], w: 0.31, old: 0.05, young: 0.6, dust: 0.4, hii: 0.5 },
  { name: '3-kpc near', pts: [[78, 3.28], [15, 3.53], [-40, 3.86]], w: 0.18, old: 0.3, young: 0.12, dust: 0.6, hii: 0.2 },
  { name: '3-kpc far', pts: [[258, 3.28], [195, 3.53], [140, 3.86]], w: 0.18, old: 0.3, young: 0.12, dust: 0.6, hii: 0.2 },
];
for (const a of ARMS) a.sorted = [...a.pts].sort((p, q) => p[0] - q[0]);

export const NARMS = ARMS.length;
export const MAXPTS = 40;

// ln R of an arm at beta (deg); also returns the end fade (0..1)
export function armLnR(arm, b) {
  const s = arm.sorted, n = s.length;
  const b0 = s[0][0], b1 = s[n - 1][0];
  const sm = (e0, e1, x) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
  const fade = sm(b0 - 3, b0 + 20, b) * (1 - sm(b1 - 20, b1 + 3, b));
  let lr = Math.log(s[0][1]);
  for (let i = 0; i < n - 1; i++) {
    if (b >= s[i][0]) {
      const t = Math.min(1, (b - s[i][0]) / (s[i + 1][0] - s[i][0]));
      lr = Math.log(s[i][1]) + (Math.log(s[i + 1][1]) - Math.log(s[i][1])) * t;
    }
  }
  return [lr, fade];
}

// uniforms describing the arms (shared by the map, disk and star shaders)
export function armUniforms() {
  const pts = [], info = [], amp = [];
  const wYoung = [];
  for (const a of ARMS) {
    info.push(new THREE.Vector4(pts.length, a.sorted.length, a.w, a.major || 0));
    for (const [b, R] of a.sorted) pts.push(new THREE.Vector2(b * D2R, Math.log(R)));
    amp.push(new THREE.Vector4(a.old, a.young, a.dust, a.hii));
    // young stars per arm ~ amplitude x angular extent
    const ext = (a.sorted[a.sorted.length - 1][0] - a.sorted[0][0]) / 360;
    wYoung.push(a.young * Math.sqrt(ext));
  }
  while (pts.length < MAXPTS) pts.push(new THREE.Vector2());
  const tot = wYoung.reduce((x, y) => x + y, 0);
  let acc = 0;
  const cum = wYoung.map((w) => (acc += w / tot));
  return {
    uArmPts: { value: pts },
    uArmInfo: { value: info },
    uArmAmp: { value: amp },
    uArmCum: { value: cum },
  };
}

// HII regions: a small catalogue (CPU, deterministic) drawn as soft pink
// sprites. Positions follow the arms (slightly downstream of the dust lanes).
export function makeHII(count, rand) {
  const out = [];
  const w = ARMS.map((a) => a.hii * (a.major ? 1.6 : 1) * Math.sqrt((a.sorted[a.sorted.length - 1][0] - a.sorted[0][0]) / 360));
  const tot = w.reduce((x, y) => x + y, 0);
  const gauss = () => Math.sqrt(-2 * Math.log(rand() + 1e-9)) * Math.cos(2 * Math.PI * rand());
  let guard = 0;
  while (out.length < count && guard++ < count * 40) {
    // central molecular zone: a few giants in the inner 200 pc
    if (rand() < 0.006) {
      const r = 0.08 + 0.12 * rand(), ph = rand() * Math.PI * 2;
      out.push({ x: r * Math.cos(ph), y: gauss() * 0.01, z: r * Math.sin(ph) * 0.6, r: 0.006 + 0.008 * rand(), L: 0.3 + 0.5 * rand() });
      continue;
    }
    let u = rand() * tot, ai = 0;
    while (ai < ARMS.length - 1 && u > w[ai]) { u -= w[ai]; ai++; }
    const arm = ARMS[ai], s = arm.sorted;
    const b = s[0][0] + (s[s.length - 1][0] - s[0][0]) * rand();
    const [lr, fade] = armLnR(arm, b);
    const R = Math.exp(lr);
    // star formation falls off outside ~10 kpc and inside the bar region
    const keep = fade * Math.min(1, Math.exp(-(R - 6) / 2.4)) * Math.exp(-Math.max(0, R - 9) / 1.6) * Math.min(1, Math.max(0, (R - 3.0) / 1.2));
    if (rand() > keep) continue;
    const width = arm.w + 0.042 * (R - 5);
    // a star-forming complex just downstream of the dust lane: 1-7 knots within ~100 pc
    const Rc = R + (0.15 + 0.35 * gauss()) * Math.max(0.12, width);
    const phc = (b + 90) * D2R + gauss() * 0.1 * width / R;
    const nk = 2 + Math.floor(rand() * rand() * 10), spread = 0.04 + 0.1 * rand();
    for (let j = 0; j < nk && out.length < count; j++) {
      // giant complexes (artistic: 0.1-0.26 kpc across, enlarged so they read at galaxy scale) + ordinary knots
      const big = rand() < (arm.major ? 0.14 : 0.07);
      const rad = big ? 0.05 + 0.08 * rand() : 0.012 * Math.exp(rand() * 1.5);
      out.push({ x: Rc * Math.cos(phc) + gauss() * spread, y: gauss() * 0.02, z: Rc * Math.sin(phc) + gauss() * spread, r: rad, L: (big ? 2.4 : 1.0) * 0.4 * Math.pow(1 - 0.96 * rand(), -0.75) }); // heavy-tailed: many faint knots, a few bright (no confetti)
    }
  }
  return out;
}

// tiny deterministic RNG (mulberry32)
export function rng(seed = 1) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
